import React, { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { api, formatErr, formatTL } from "@/lib/api";

const EMPTY = {
  name: "", slug: "", category_slug: "mumlar", price: 0, sale_price: null,
  sku: "", stock: 0, images: [], short_description: "", description: "",
  scent_notes: "", ingredients: "", size: "", burning_time: "", skin_suitability: "",
  customizable: false, published: true, featured: false, tags: [],
  seo_title: "", seo_description: "",
};

export default function AdminProducts() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    setLoading(true);
    const [p, c] = await Promise.all([api.get("/admin/products"), api.get("/categories")]);
    setProducts(p.data); setCategories(c.data); setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    if (!confirm("Bu ürünü silmek istediğinize emin misiniz?")) return;
    await api.delete(`/admin/products/${id}`); toast.success("Silindi"); load();
  };

  return (
    <div data-testid="admin-products">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-4xl">Ürünler</h1>
          <p className="text-larc-muted mt-1">Atölyenizin tüm ürünleri.</p>
        </div>
        <button onClick={() => setEditing({ ...EMPTY })} className="inline-flex items-center gap-2 bg-larc-ink text-white rounded-full px-6 py-3 text-sm hover:bg-larc-gold transition-colors" data-testid="add-product-btn">
          <Plus size={16} /> Yeni Ürün
        </button>
      </div>

      <div className="mt-8 bg-white border border-larc-border rounded-2xl overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-larc-muted">Yükleniyor…</div>
        ) : products.length === 0 ? (
          <div className="p-10 text-center text-larc-muted">Henüz ürün yok</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-larc-alt text-larc-muted">
              <tr>
                <th className="text-left px-6 py-3">Ürün</th>
                <th className="text-left px-6 py-3">Kategori</th>
                <th className="text-left px-6 py-3">Fiyat</th>
                <th className="text-left px-6 py-3">Stok</th>
                <th className="text-left px-6 py-3">Durum</th>
                <th className="text-right px-6 py-3">İşlem</th>
              </tr>
            </thead>
            <tbody>
              {products.map((p) => (
                <tr key={p.id} className="border-t border-larc-border" data-testid={`row-${p.slug}`}>
                  <td className="px-6 py-3">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-lg overflow-hidden bg-larc-alt shrink-0">
                        {p.images?.[0] && <img src={p.images[0]} alt="" className="w-full h-full object-cover" />}
                      </div>
                      <div>
                        <div className="font-medium">{p.name}</div>
                        <div className="text-xs text-larc-muted">{p.sku}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-3">{p.category_slug}</td>
                  <td className="px-6 py-3">
                    {p.sale_price && p.sale_price < p.price ? (
                      <>
                        <span className="line-through text-larc-muted mr-2">{formatTL(p.price)}</span>
                        <span>{formatTL(p.sale_price)}</span>
                      </>
                    ) : formatTL(p.price)}
                  </td>
                  <td className="px-6 py-3">
                    <span className={p.stock <= 5 ? "text-orange-600" : ""}>{p.stock}</span>
                  </td>
                  <td className="px-6 py-3">
                    {p.published ? <span className="text-xs px-2 py-1 rounded-full bg-green-50 text-green-700 border border-green-100">Yayında</span> : <span className="text-xs px-2 py-1 rounded-full bg-larc-alt text-larc-muted">Taslak</span>}
                    {p.featured && <span className="ml-2 text-xs px-2 py-1 rounded-full bg-larc-gold/10 text-larc-gold border border-larc-gold/30">Öne Çıkan</span>}
                  </td>
                  <td className="px-6 py-3 text-right space-x-2">
                    <button onClick={() => setEditing(p)} className="inline-flex items-center gap-1.5 text-larc-muted hover:text-larc-ink text-sm" data-testid={`edit-${p.slug}`}><Pencil size={14} /> Düzenle</button>
                    <button onClick={() => remove(p.id)} className="inline-flex items-center gap-1.5 text-larc-muted hover:text-red-600 text-sm" data-testid={`delete-${p.slug}`}><Trash2 size={14} /> Sil</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {editing && (
        <ProductForm
          initial={editing}
          categories={categories}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function ProductForm({ initial, categories, onClose, onSaved }) {
  const isNew = !initial.id;
  const [form, setForm] = useState(() => ({
    ...EMPTY,
    ...initial,
    images: initial.images || [],
    tags: initial.tags || [],
  }));
  const [imagesText, setImagesText] = useState((initial.images || []).join("\n"));
  const [tagsText, setTagsText] = useState((initial.tags || []).join(", "));
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    const body = {
      ...form,
      price: Number(form.price) || 0,
      sale_price: form.sale_price === "" || form.sale_price === null ? null : Number(form.sale_price),
      stock: Number(form.stock) || 0,
      images: imagesText.split("\n").map((s) => s.trim()).filter(Boolean),
      tags: tagsText.split(",").map((s) => s.trim()).filter(Boolean),
    };
    try {
      if (isNew) await api.post("/admin/products", body);
      else await api.put(`/admin/products/${initial.id}`, body);
      toast.success("Kaydedildi");
      onSaved();
    } catch (err) {
      toast.error(formatErr(err));
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center overflow-y-auto p-4" data-testid="product-form">
      <div className="bg-white rounded-2xl w-full max-w-3xl my-8">
        <div className="flex items-center justify-between p-6 border-b border-larc-border">
          <h2 className="font-heading text-2xl">{isNew ? "Yeni Ürün" : "Ürünü Düzenle"}</h2>
          <button onClick={onClose} className="text-larc-muted hover:text-larc-ink"><X size={18} /></button>
        </div>
        <form onSubmit={save} className="p-6 grid grid-cols-1 md:grid-cols-2 gap-5">
          <F label="Ürün Adı" required>
            <input required value={form.name} onChange={(e) => set("name", e.target.value)} className="inp" data-testid="form-name" />
          </F>
          <F label="Slug (URL)" required>
            <input required value={form.slug} onChange={(e) => set("slug", e.target.value)} className="inp" data-testid="form-slug" />
          </F>
          <F label="Kategori" required>
            <select required value={form.category_slug} onChange={(e) => set("category_slug", e.target.value)} className="inp" data-testid="form-category">
              {categories.map((c) => <option key={c.slug} value={c.slug}>{c.name}</option>)}
            </select>
          </F>
          <F label="SKU"><input value={form.sku} onChange={(e) => set("sku", e.target.value)} className="inp" /></F>
          <F label="Fiyat" required><input required type="number" step="0.01" value={form.price} onChange={(e) => set("price", e.target.value)} className="inp" data-testid="form-price" /></F>
          <F label="İndirimli Fiyat"><input type="number" step="0.01" value={form.sale_price ?? ""} onChange={(e) => set("sale_price", e.target.value)} className="inp" /></F>
          <F label="Stok"><input type="number" value={form.stock} onChange={(e) => set("stock", e.target.value)} className="inp" data-testid="form-stock" /></F>
          <F label="Boyut / Hacim"><input value={form.size} onChange={(e) => set("size", e.target.value)} className="inp" /></F>
          <F label="Yanma Süresi"><input value={form.burning_time} onChange={(e) => set("burning_time", e.target.value)} className="inp" /></F>
          <F label="Cilt Uygunluğu"><input value={form.skin_suitability} onChange={(e) => set("skin_suitability", e.target.value)} className="inp" /></F>
          <F label="Koku Notaları" full><input value={form.scent_notes} onChange={(e) => set("scent_notes", e.target.value)} className="inp" /></F>
          <F label="İçindekiler" full><input value={form.ingredients} onChange={(e) => set("ingredients", e.target.value)} className="inp" /></F>
          <F label="Kısa Açıklama" full><textarea rows={2} value={form.short_description} onChange={(e) => set("short_description", e.target.value)} className="inp" /></F>
          <F label="Açıklama" full><textarea rows={4} value={form.description} onChange={(e) => set("description", e.target.value)} className="inp" /></F>
          <F label="Görseller (her satıra bir URL)" full><textarea rows={3} value={imagesText} onChange={(e) => setImagesText(e.target.value)} className="inp" data-testid="form-images" /></F>
          <F label="Etiketler (virgülle ayır)" full><input value={tagsText} onChange={(e) => setTagsText(e.target.value)} className="inp" /></F>
          <F label="SEO Başlık" full><input value={form.seo_title} onChange={(e) => set("seo_title", e.target.value)} className="inp" /></F>
          <F label="SEO Açıklama" full><textarea rows={2} value={form.seo_description} onChange={(e) => set("seo_description", e.target.value)} className="inp" /></F>

          <div className="md:col-span-2 flex flex-wrap gap-6 mt-2">
            <Check label="Yayında" checked={form.published} onChange={(v) => set("published", v)} testid="form-published" />
            <Check label="Öne Çıkan" checked={form.featured} onChange={(v) => set("featured", v)} testid="form-featured" />
            <Check label="Kişiye Özel" checked={form.customizable} onChange={(v) => set("customizable", v)} testid="form-customizable" />
          </div>

          <div className="md:col-span-2 flex items-center justify-end gap-3 mt-4 pt-4 border-t border-larc-border">
            <button type="button" onClick={onClose} className="px-6 py-2.5 rounded-full border border-larc-border text-sm">İptal</button>
            <button disabled={saving} className="px-6 py-2.5 rounded-full bg-larc-ink text-white text-sm hover:bg-larc-gold transition-colors disabled:opacity-50" data-testid="form-submit">
              {saving ? "Kaydediliyor…" : "Kaydet"}
            </button>
          </div>
        </form>
        <style>{`.inp{width:100%;border:1px solid #EBE6DE;border-radius:8px;padding:10px 12px;font-size:14px;outline:none;background:#fff} .inp:focus{border-color:#B8A386}`}</style>
      </div>
    </div>
  );
}

function F({ label, children, full, required }) {
  return (
    <label className={`block ${full ? "md:col-span-2" : ""}`}>
      <span className="text-xs uppercase tracking-[0.15em] text-larc-muted">{label}{required && " *"}</span>
      <div className="mt-2">{children}</div>
    </label>
  );
}

function Check({ label, checked, onChange, testid }) {
  return (
    <label className="inline-flex items-center gap-2 text-sm">
      <input type="checkbox" checked={!!checked} onChange={(e) => onChange(e.target.checked)} className="accent-larc-gold w-4 h-4" data-testid={testid} />
      {label}
    </label>
  );
}
